import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, router } from '@inertiajs/react';

const inputClass =
  "block w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition";

function formatPrice(cents) {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

const itemKey = (item) => `${item.role}|${item.training_type}`;
const labelFor = (item) => `${item.role} · ${item.training_type.replace(/_/g, ' ')}`;

// Deals are create-only. A deal's link may already be with a customer, so its
// terms can never change after creation — to change an offer, withdraw it and
// create a new one.
export default function DealForm({
  deal, availableItems = [], availableFacilityTypes = [], availablePlans = [],
  errors = [], facilitySearchPath = null,
  submitPath = '/superadmin/deals',
  cancelPath = '/superadmin/deals',
}) {
  const [name, setName] = useState(deal.name || '');
  const [notes, setNotes] = useState(deal.notes || '');
  const [plan, setPlan] = useState(deal.plan || 'subscription');
  const [facilityType, setFacilityType] = useState(deal.facility_type || availableFacilityTypes[0]?.value || '');
  const [priceDollars, setPriceDollars] = useState(deal.price_dollars || '');
  const [expiresAt, setExpiresAt] = useState(deal.expires_at || '');
  const [items, setItems] = useState(
    (deal.items || []).map((it) => ({ ...it, key: itemKey(it) }))
  );
  const [submitting, setSubmitting] = useState(false);

  // Optional target facility. When set, the offer is private to that facility's
  // owner and its type dictates the deal's facility type.
  const [targetFacility, setTargetFacility] = useState(deal.facility || null);
  const [facilityQuery, setFacilityQuery] = useState('');
  const [facilityResults, setFacilityResults] = useState([]);
  const [facilitySearching, setFacilitySearching] = useState(false);
  const [facilitySearchError, setFacilitySearchError] = useState(null);
  const searchSeq = useRef(0);

  useEffect(() => {
    if (!facilitySearchPath || targetFacility) return undefined;
    const q = facilityQuery.trim();
    if (q.length < 2) {
      setFacilityResults([]);
      setFacilitySearchError(null);
      return undefined;
    }

    const seq = ++searchSeq.current;
    setFacilitySearching(true);
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(`${facilitySearchPath}?q=${encodeURIComponent(q)}`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('search failed');
        const data = await response.json();
        // Ignore a slow response that lost the race to a newer keystroke.
        if (seq !== searchSeq.current) return;
        setFacilityResults(data.results || []);
        setFacilitySearchError(null);
      } catch {
        if (seq !== searchSeq.current) return;
        setFacilityResults([]);
        setFacilitySearchError('Could not search facilities. Check your connection and try again.');
      } finally {
        if (seq === searchSeq.current) setFacilitySearching(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [facilityQuery, facilitySearchPath, targetFacility]);

  const selectFacility = (row) => {
    if (!row.has_owner) return;
    setTargetFacility(row);
    // Mirrors the server-side derive-and-lock in DealAuthoring#apply_target_facility!.
    setFacilityType(row.facility_type);
    setFacilityQuery('');
    setFacilityResults([]);
  };

  const clearFacility = () => {
    setTargetFacility(null);
    setFacilityQuery('');
    setFacilityResults([]);
  };

  const unusedItems = useMemo(
    () => availableItems.filter((a) => !items.some((i) => itemKey(i) === itemKey(a))),
    [availableItems, items]
  );

  const totalSeats = useMemo(
    () => items.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0),
    [items]
  );

  const priceCents = useMemo(() => {
    const parsed = Number.parseFloat(priceDollars);
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
  }, [priceDollars]);

  const addItem = (defn) => {
    setItems((prev) => [...prev, { role: defn.role, training_type: defn.training_type, quantity: 1, key: itemKey(defn) }]);
  };

  const updateItem = (index, patch) => {
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = {
      deal: {
        name: name.trim(),
        notes: notes.trim(),
        plan,
        facility_type: facilityType,
        facility_id: targetFacility?.id ?? null,
        price_dollars: priceDollars.trim(),
        expires_at: expiresAt,
        items_attributes: items.map((it) => ({
          role: it.role, training_type: it.training_type, quantity: Number(it.quantity) || 0,
        })),
      },
    };

    router.post(submitPath, payload, { onFinish: () => setSubmitting(false) });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <ul className="list-disc list-inside text-sm text-red-800 space-y-0.5">
            {errors.map((err) => <li key={err}>{err}</li>)}
          </ul>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-5">
        <h2 className="text-lg font-semibold text-gray-900">Offer</h2>

        <div>
          <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-1.5">Internal name</label>
          <input id="name" className={inputClass} value={name} onChange={(e) => setName(e.target.value)}
                 required autoFocus placeholder="e.g. Sunny Care — 3 caregiver seats" />
          <p className="mt-1 text-xs text-gray-500">Shown to the customer as the offer title.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="plan" className="block text-sm font-semibold text-gray-700 mb-1.5">Plan</label>
            <select id="plan" className={inputClass} value={plan} onChange={(e) => setPlan(e.target.value)}>
              {availablePlans.map((p) => (
                <option key={p} value={p}>{p === 'subscription' ? 'Subscription' : 'One-time'}</option>
              ))}
            </select>
          </div>
        </div>

        {facilitySearchPath && (
          <div>
            <label htmlFor="facility_search" className="block text-sm font-semibold text-gray-700 mb-1.5">
              Existing customer <span className="font-normal text-gray-400">(optional)</span>
            </label>

            {targetFacility ? (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3.5 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 break-words">{targetFacility.name}</p>
                    <p className="text-xs text-gray-600">
                      {[targetFacility.location, targetFacility.facility_type_label].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <button type="button" onClick={clearFacility}
                          className="shrink-0 text-xs font-semibold text-indigo-700 hover:text-indigo-900 cursor-pointer">
                    Clear
                  </button>
                </div>
                <p className="mt-2 text-xs text-indigo-900">
                  Only <strong>{targetFacility.name}</strong>'s owner can redeem this link. The seats land in
                  their pool to assign — no new account is created.
                  {plan === 'subscription' && ' This creates a new, separate subscription; their existing one is untouched.'}
                </p>
              </div>
            ) : (
              <>
                <input id="facility_search" className={inputClass} value={facilityQuery} autoComplete="off"
                       onChange={(e) => setFacilityQuery(e.target.value)}
                       placeholder="Search by facility name or city…" />
                <p className="mt-1 text-xs text-gray-500">
                  Leave empty to create an open link any new buyer can redeem.
                </p>

                {facilitySearchError && (
                  <p className="mt-2 text-xs text-red-700">{facilitySearchError}</p>
                )}
                {facilitySearching && !facilitySearchError && (
                  <p className="mt-2 text-xs text-gray-500">Searching…</p>
                )}
                {!facilitySearching && !facilitySearchError && facilityQuery.trim().length >= 2 && facilityResults.length === 0 && (
                  <p className="mt-2 text-xs text-gray-500">No facilities match "{facilityQuery.trim()}".</p>
                )}

                {facilityResults.length > 0 && (
                  <ul className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-200 overflow-hidden">
                    {facilityResults.map((row) => (
                      <li key={row.id}>
                        <button type="button" onClick={() => selectFacility(row)} disabled={!row.has_owner}
                                className="w-full text-left px-3.5 py-2.5 hover:bg-gray-50 disabled:hover:bg-white disabled:cursor-not-allowed cursor-pointer">
                          <span className="block text-sm text-gray-900 break-words">{row.name}</span>
                          <span className="block text-xs text-gray-500">
                            {[row.location, row.facility_type_label].filter(Boolean).join(' · ')}
                          </span>
                          {!row.has_owner && (
                            <span className="block mt-0.5 text-xs text-amber-700">
                              No owner yet — invite an owner before targeting a deal here.
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}

        <div>
          <label htmlFor="facility_type" className="block text-sm font-semibold text-gray-700 mb-1.5">Facility type</label>
          <select id="facility_type" className={`${inputClass} disabled:bg-gray-100 disabled:text-gray-500`}
                  value={facilityType} disabled={Boolean(targetFacility)}
                  onChange={(e) => setFacilityType(e.target.value)}>
            {availableFacilityTypes.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          {targetFacility && (
            <p className="mt-1 text-xs text-gray-500">Locked to {targetFacility.name}'s facility type.</p>
          )}
        </div>

        {plan === 'subscription' && (
          <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5">
            The deal price applies to the customer's <strong>first payment only</strong>. Renewals bill at
            the standard price — the customer is told this on the offer page.
          </p>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-semibold text-gray-900">Licenses</h2>
          <div className="flex gap-1.5 flex-wrap">
            {unusedItems.map((defn) => (
              <button key={itemKey(defn)} type="button" onClick={() => addItem(defn)}
                      className="rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 cursor-pointer">
                + {labelFor(defn)}
              </button>
            ))}
          </div>
        </div>

        {items.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">Add at least one license to the deal.</p>
        ) : (
          <div className="space-y-3">
            {items.map((item, index) => (
              <div key={item.key} className="flex items-center gap-3 rounded-xl border border-gray-200 p-3">
                <span className="flex-1 text-sm text-gray-900">{labelFor(item)}</span>
                <label className="text-xs text-gray-500" htmlFor={`qty_${index}`}>Seats</label>
                <input id={`qty_${index}`} type="number" min="1" step="1" value={item.quantity}
                       onChange={(e) => updateItem(index, { quantity: e.target.value })}
                       className="w-24 rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm" />
                <button type="button" onClick={() => removeItem(index)}
                        className="text-xs font-semibold text-red-700 hover:text-red-800 cursor-pointer">
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-5">
        <h2 className="text-lg font-semibold text-gray-900">Price</h2>

        <div>
          <label htmlFor="price_dollars" className="block text-sm font-semibold text-gray-700 mb-1.5">
            Locked-in total (USD)
          </label>
          <input id="price_dollars" className={inputClass} value={priceDollars} inputMode="decimal"
                 onChange={(e) => setPriceDollars(e.target.value)} required placeholder="199.00" />
          <p className="mt-1 text-xs text-gray-500">
            The exact amount the customer pays for everything above — {totalSeats} seat{totalSeats === 1 ? '' : 's'} total.
          </p>
        </div>

        {priceCents != null && (
          <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-700">
            Customer pays <span className="font-semibold text-indigo-700">{formatPrice(priceCents)}</span>
            {plan === 'subscription' ? ' on their first payment' : ' in total'}.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="expires_at" className="block text-sm font-semibold text-gray-700 mb-1.5">
              Expires <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input id="expires_at" type="date" className={inputClass} value={expiresAt}
                   onChange={(e) => setExpiresAt(e.target.value)} />
          </div>
        </div>

        <div>
          <label htmlFor="notes" className="block text-sm font-semibold text-gray-700 mb-1.5">
            Internal notes <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <textarea id="notes" rows={3} className={inputClass} value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Who this is for, what was agreed, who approved it." />
        </div>
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-3">
        <Link href={cancelPath}
              className="flex-1 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition">
          Cancel
        </Link>
        <button type="submit" disabled={submitting || items.length === 0}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition cursor-pointer">
          {submitting ? 'Saving…' : 'Create deal'}
        </button>
      </div>
    </form>
  );
}
